/**
 * System/platform automations store mail fields on step.config (ensureDefaults)
 * or step.config.config (AutomationEditor). Merge both when reading.
 */
export type ActionStepEmailFields = {
  subject: string;
  htmlBody: string;
  fromEmail: string;
  fromName: string;
};

function pickString(
  root: Record<string, unknown>,
  nested: Record<string, unknown> | null,
  key: string
): string {
  const nestedVal = nested?.[key];
  if (typeof nestedVal === 'string' && nestedVal.trim()) return nestedVal;
  const rootVal = root[key];
  if (typeof rootVal === 'string' && rootVal.trim()) return rootVal;
  return '';
}

export function resolveActionStepEmailConfig(stepConfig: unknown): ActionStepEmailFields {
  const root =
    stepConfig && typeof stepConfig === 'object'
      ? (stepConfig as Record<string, unknown>)
      : {};
  const nested =
    root.config && typeof root.config === 'object'
      ? (root.config as Record<string, unknown>)
      : null;

  return {
    subject: pickString(root, nested, 'subject'),
    htmlBody: pickString(root, nested, 'htmlBody'),
    fromEmail: pickString(root, nested, 'fromEmail'),
    fromName: pickString(root, nested, 'fromName'),
  };
}

export function isLikelyEmailActionStep(step: {
  type?: string;
  title?: string;
  config?: unknown;
}): boolean {
  if (step.type !== 'action') return false;
  const fields = resolveActionStepEmailConfig(step.config);
  if (fields.htmlBody || fields.subject) return true;
  const title = (step.title || '').toLowerCase();
  return /email|mail|send|welcome|verify|reset|note|notification/.test(title);
}

/** Write mail fields to both root and nested.config for compatibility. */
export function buildActionStepEmailConfigUpdate(
  existingConfig: Record<string, unknown>,
  fields: Partial<ActionStepEmailFields>
): Record<string, unknown> {
  const nested =
    existingConfig.config && typeof existingConfig.config === 'object'
      ? { ...(existingConfig.config as Record<string, unknown>) }
      : {};

  const merged = {
    ...existingConfig,
    ...fields,
    config: {
      ...nested,
      ...fields,
    },
  };

  return merged;
}
